import { useCallback, useState } from 'react';

/**
 * Drag-to-zoom + reset (autoscale) for a single Recharts panel, following Recharts' own
 * "Highlight and Zoom" pattern: track a left/right X value pair while the mouse is down,
 * commit it as the XAxis domain on mouse-up, and expose a resetZoom() that clears back to
 * baseDomain. X-axis only (not Y) — dual-Y-axis charts would need reverse-mapping a drag
 * box to two independent Y scales, real complexity for a use case (zooming into a time/
 * speed/RPM range) that's about the X axis.
 *
 * baseDomain defaults to ['dataMin', 'dataMax'] (the vast majority of charts on this site);
 * pass an explicit domain (e.g. [0, 'dataMax']) for a chart whose un-zoomed X axis is
 * intentionally pinned to a fixed start, like GearRatioCalculator's RPM axis.
 *
 * One hook call = one independent chart's zoom state, so call it once per chart panel, not
 * once per file — a component rendered multiple times (e.g. Envelope in MotorEnvelopeChart,
 * or MiniLine across several charts) gets independent zoom per instance for free, same as
 * any other hook.
 *
 * This is the standard, site-wide pattern for every Recharts line/area/composed chart with
 * a continuous numeric X axis — apply it (paired with ChartZoomHeader for the UI) to any
 * new chart of that kind. Skip it for categorical/bar charts (e.g. a 2-bar comparison) —
 * there's no continuous range to drag-zoom into.
 */
export function useChartZoom(baseDomain = ['dataMin', 'dataMax'], initialDomain = null) {
  const [refAreaLeft, setRefAreaLeft] = useState(null);
  const [refAreaRight, setRefAreaRight] = useState(null);
  // null = full range (autoscale). initialDomain (optional) opens the chart already zoomed —
  // e.g. the Regen Braking Simulator's event charts show one cycle pass of a multi-hour run;
  // Reset Zoom still returns to the full range.
  const [domain, setDomain] = useState(initialDomain);

  const onMouseDown = useCallback((e) => {
    if (e && e.activeLabel !== undefined) setRefAreaLeft(e.activeLabel);
  }, []);

  const onMouseMove = useCallback((e) => {
    if (refAreaLeft !== null && e && e.activeLabel !== undefined) setRefAreaRight(e.activeLabel);
  }, [refAreaLeft]);

  const onMouseUp = useCallback(() => {
    if (refAreaLeft === null || refAreaRight === null || refAreaLeft === refAreaRight) {
      setRefAreaLeft(null);
      setRefAreaRight(null);
      return;
    }
    const [left, right] = refAreaLeft < refAreaRight ? [refAreaLeft, refAreaRight] : [refAreaRight, refAreaLeft];
    setDomain([left, right]);
    setRefAreaLeft(null);
    setRefAreaRight(null);
  }, [refAreaLeft, refAreaRight]);

  const resetZoom = useCallback(() => {
    setDomain(null);
    setRefAreaLeft(null);
    setRefAreaRight(null);
  }, []);

  return {
    xDomain: domain ?? baseDomain,
    isZoomed: domain !== null,
    isSelecting: refAreaLeft !== null && refAreaRight !== null,
    refAreaLeft,
    refAreaRight,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    resetZoom,
  };
}
